import React, { useState, useEffect } from 'react';
import { Box, Divider, List, ListItemButton, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { getNotificationsByType } from '@services/notificationService';
import { formatDate } from '@utils/dateHelper';
import { useTranslation } from 'react-i18next';
import type { Notification } from '@app-types/models/chat';
import { PageContainer, PageHeader, SectionCard, EmptyState, LoadingState } from '../ui';
import { brand } from '../../theme/tokens';

const notificationIcon = (type: string) => {
  switch (type) {
    case 'reminder':
    case 'appointment':
      return <CalendarMonthOutlinedIcon />;
    case 'payment':
      return <CreditCardOutlinedIcon />;
    case 'offer':
      return <LocalOfferOutlinedIcon />;
    case 'system':
      return <CampaignOutlinedIcon />;
    default:
      return <NotificationsNoneOutlinedIcon />;
  }
};

interface NotificationRowData {
  id: string;
  type: string;
  content: string;
  timestamp: string;
  isNew: boolean;
  subject?: string;
  link?: string;
}

interface NotificationRowProps {
  notification: NotificationRowData;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification }) => (
  <ListItemButton
    sx={{
      minHeight: 44,
      alignItems: 'flex-start',
      gap: 1.5,
      px: { xs: 2, sm: 2.5 },
      py: 1.5,
      '&:active': { bgcolor: brand.goldSoft },
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        color: 'primary.dark',
        background: brand.goldSoft,
        '& svg': { fontSize: 22 },
      }}
    >
      {notificationIcon(notification.type)}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      {notification.subject && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: notification.isNew ? 700 : 600, lineHeight: 1.3 }}
        >
          {notification.subject}
        </Typography>
      )}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: notification.isNew ? 600 : 400 }}
      >
        {notification.content}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {notification.timestamp}
      </Typography>
    </Box>
    {notification.isNew && (
      <Box sx={{ flexShrink: 0, width: 10, height: 10, borderRadius: '50%', bgcolor: brand.gold, mt: 0.75 }} />
    )}
  </ListItemButton>
);

const NotificationPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setIsLoading(true);
        const notificationList = await getNotificationsByType(type!);
        setNotifications(Array.isArray(notificationList) ? notificationList : []);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [type]);

  return (
    <PageContainer>
      <PageHeader
        title={t('notificationPage.title', t('notification', 'Notifications'))}
        icon={<NotificationsIcon />}
      />

      {isLoading ? (
        <LoadingState variant="list" count={5} />
      ) : notifications.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<NotificationsIcon />}
            title={t('notificationPage.emptyTitle', "You're all caught up")}
            description={t(
              'notificationPage.emptyDescription',
              'There are no notifications of this type right now.'
            )}
          />
        </SectionCard>
      ) : (
        <SectionCard disableContentPadding>
          <List sx={{ p: 0 }} className="notification-list-container">
            {notifications.map((notification, index) => (
              <Box key={notification._id}>
                {index > 0 && <Divider component="li" sx={{ ml: { xs: 8, sm: 8.5 } }} />}
                <NotificationRow
                  notification={{
                    id: notification._id,
                    type: notification.type,
                    content: notification.content,
                    timestamp: formatDate(notification.createdAt),
                    isNew: !notification.read,
                    subject: notification.subject,
                    link: notification.link,
                  }}
                />
              </Box>
            ))}
          </List>
        </SectionCard>
      )}
    </PageContainer>
  );
};

export default NotificationPage;
